import type { StaticEntityType } from "@config";
import type {
  Dispatch,
  JSX,
  MutableRefObject,
  ReactNode,
  SetStateAction,
} from "react";

export enum TableHeaderFontStyle {
  Regular = "regular",
  Italic = "italic",
  Bold = "bold",
}

export enum TableHeaderPosition {
  Top = "top",
  Bottom = "bottom",
  Left = "left",
  Right = "right",
}

export enum TableBorderStyle {
  Solid = "solid",
  Dashed = "dashed",
  Dotted = "dotted",
}

export enum TableCellVerticalAlignment {
  Top = "start",
  Center = "center",
  Bottom = "end",
}

export enum TableCellHorizontalAlignment {
  Left = "start",
  Center = "center",
  Right = "end",
}

export enum TableBorderRadiusEnum {
  None = "none",
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export interface ITableCellBorder {
  width?: number;
  color?: string;
  style?: TableBorderStyle;
}

export interface ITableCell {
  id: string;
  colSpan: number;
  rowSpan: number;
  mergedInto?: string; // ID of the parent (top-left) cell this cell is merged into

  content: string;

  hasFroalaFontSize?: boolean;

  bgColor?: string;
  verticalAlignment?: TableCellVerticalAlignment;
  // Horizontal alignment for the cell's stacked content (in-cell elements
  // narrower than the cell). Unset = stretch.
  horizontalAlignment?: TableCellHorizontalAlignment;
  textRotation?: "none" | "up" | "down";
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: TableBorderStyle;

  // Borders
  // Top
  borderTopColor?: string;
  borderTopWidth?: number;
  borderTopStyle?: TableBorderStyle;
  //
  // Bottom
  borderBottomColor?: string;
  borderBottomWidth?: number;
  borderBottomStyle?: TableBorderStyle;
  //
  // Left
  borderLeftColor?: string;
  borderLeftWidth?: number;
  borderLeftStyle?: TableBorderStyle;
  //
  // Right
  borderRightColor?: string;
  borderRightWidth?: number;
  borderRightStyle?: TableBorderStyle;

  // Renders the formula result at render time. Mutually exclusive with `embedded`.
  calculation?: ITableCellCalculation;

  // Author-applied formatting for the calc result; deepest text node is the value placeholder.
  calcFormatHtml?: string;

  // Renders an embedded Storydoc element. Mutually exclusive with `calculation`.
  embedded?: StaticEntityType;

  // Grouped ("mini-canvas") cell. EDITOR uses `groupId` (entity ref); PUBLISHED inlines `group`.
  groupId?: string;
  group?: StaticEntityType;
  // Denormalized sort/filter key from the leading RichText child; only meaningful when groupSortable.
  groupText?: string;
  // True iff the group's leading child is RichText (drives sort/filter gates).
  groupSortable?: boolean;
}

export interface ITableCellCalculation {
  /** Formula string, e.g. "=SUM(A1:C3)". Parsed/evaluated at render time. */
  formula: string;
}

export interface ITableCopiedCell {
  col: number;
  row: number;
  data: ITableCell;
}

export interface ITableRow {
  id: string;
  cells: ITableCell[];
}

export type ITableData = ITableCell[];

export interface ITableSpecificSettings {
  tableCols: number;
  tableColWidths: number[];
  tableRows: number;
  tableRowHeights: number[];
  tableData: ITableData;
  //
  // Header
  tableHasHeader: boolean;
  tableHeaderPosition: TableHeaderPosition;
  tableHeaderBgColor: string;
  tableHeaderBorderColor: string;
  tableHeaderTextColor: string;
  //
  // Alternating rows
  tableHasAlternatingRows: boolean;
  tableAlternatingRowsBgColor: string;
  tableAlternatingRowsBorderColor: string;
  tableAlternatingRowsTextColor: string;
  //
  tableCellFontSize: number;
  tableCellTextColor: string;
  tableCellBgColor: string;
  tableCellBorderColor: string;
  tableCellBorderStyle: TableBorderStyle;
  tableCellBorderWidth: number;
  tableCellVerticalAlignment: TableCellVerticalAlignment;
  tableCellTextAlignment: "left" | "center" | "right" | "justify";
  tableCellTextOrientation: "ltr" | "rtl";

  tableMobileLayout: "card" | "table";

  tableAllowSorting: boolean;
  tableAllowFiltering: boolean;

  tableFrozenRowsCount?: number;

  round: TableBorderRadiusEnum;
}

export interface ITableSettings extends ITableSpecificSettings {
  name: string;
  width: string;
  height: number;
  horizontalAlignment: string;
  listDynamicVariable?: string;
  /** Set on a VERSION's content copy when the sender converted the table to
   * static: its cells are the version's own again (editable, styled), no
   * longer version payload — see version-binding/version-element-data.ts. */
  versionStatic?: boolean;
  /**
   * A `data`-type variable bound to the WHOLE table, stored as its `{{token}}`.
   *
   * Distinct from `listDynamicVariable`: that one clones a single template row
   * per record, substituting `[Key]` placeholders (see
   * parse-table-list-dynamic-var.ts), whereas a data variable carries a 2D
   * matrix that replaces the table's cells/rows/columns outright. Kept in
   * `{{token}}` form so the deck scan that builds the fs-vars manifest
   * (collect-story-variables.ts) still sees the variable and the share dialog
   * renders a row for it.
   */
  dataDynamicVariable?: string;
  // Linked external data source (CSV / Sheets / Excel). Structural here so the
  // static library stays independent of the editor's provider types — the
  // concrete union lives in table-data-source-utils (AnyTableSource).
  dataSource?: { provider: string; [key: string]: unknown } | null;
}

export interface ITableCellSelection {
  x: number;
  y: number;
  width: number;
  height: number;

  startCellEl: HTMLElement;
  endCellEl: HTMLElement;

  startCellId: string;
  endCellId: string;

  cellsIds: string[];

  startCol: number;
  startRow: number;
  endCol: number;
  endRow: number;

  startCellDimensions: {
    width: number;
    height: number;
  };
}

export interface ITableCellFroalaProps {
  cell: ITableCell;
}

export interface ITableCellObserversProps {
  cellRef: MutableRefObject<HTMLElement | null>;
  // Synthesize toolbar refresh/destroy for cells with no Froala instance
  // (calc cells, cells hosting an in-cell element stack).
  emitToolbarRefresh?: boolean;
}

// Editor-injected live renderer for a cell's in-cell element stack (reads the
// derived children index from the store; @library stays store-free).
export interface ITableCellStackProps {
  cellId: string;
  // Overrides the table ctx entityId — used by the cards view, which renders
  // outside the TableCtxProvider.
  tableId?: string;
}

export interface ITableCellDropTargetProps {
  tableId: string;
  cellId: string;
  cell: Pick<ITableCell, "content">;
  canDrop: boolean;
  hasStackChildren?: boolean;
  registerSetNodeRef: (setNodeRef: (node: HTMLElement | null) => void) => void;
}

// Formula-edit state: which cell is being formula-edited + its live buffer.
export interface IFormulaEditState {
  entityId: string;
  cellId: string;
  buffer: string;
}

export interface ITableEditorData {
  hoverdCol: number | null;
  hoverdRow: number | null;
  selection: ITableCellSelection | null;
  setSelection: Dispatch<SetStateAction<ITableCellSelection | null>>;
  updateSelection: () => void;
  //
  isTableActive: boolean;
  activeCellId: string | null;
  // Full set of selected cells, independent of whether they form a contiguous
  // rect (the rect-only `selection` is null in non-contiguous/cmd-click cases).
  activeCellsIds: string[];
  navigationMode: boolean;
  setNavigationMode: Dispatch<SetStateAction<boolean>>;
  cellSelectionSlot: ReactNode;
  tableSelectBtnSlot: ReactNode;
  FroalaEditor: (props: ITableCellFroalaProps) => JSX.Element;
  TableCellEditorObserver: (props: ITableCellObserversProps) => null;
  // cellId → in-cell element child ids (tables 2.0), supplied by the
  // store-connected editor; drives per-cell Froala suppression.
  cellChildrenIndex?: Record<string, string[]>;
  // Formula-edit state + actions injected from @editor so @library stays
  // store-free (the store/selectors can't be used in the static-library reader).
  formulaEdit: IFormulaEditState | null;
  startFormulaEdit: (payload: IFormulaEditState) => void;
  updateFormulaEdit: (buffer: string) => void;
  stopFormulaEdit: () => void;
  //
  width: string;
  height: number;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
  resizeSlot: ReactNode;
  //
  // Drag reorder
  onGripMouseDown?: (
    type: "row" | "col",
    index: number,
    event: React.MouseEvent,
  ) => void;
  isDragging?: boolean;
  dragType?: "row" | "col" | null;
  dragIndex?: number | null;
  targetIndex?: number | null;
  dragIndicatorSlot?: ReactNode;
}

export interface ITableListDynamicVarData {
  data: Record<string, string>[];
}

export interface ITableCellContentAppliableSettings {
  textColor?: string;
  fontSize?: string | number;
  textAlign?: string;
  textOrientation?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  clearFormatting?: boolean;
  lineHeight?: string | number;
  textStyle?:
    | "display"
    | "slide-title"
    | "heading"
    | "subheading"
    | "normal"
    // Legacy alias kept for backward compatibility — see RichTextStyle.
    | "subtitle";
}
